import { useTranslation } from 'react-i18next';

import { GenericLayout } from '@/components/layouts/GenericLayout/GenericLayout';
import { Head } from '@/components/seo/Head/Head';
import { Button } from '@/components/ui/Button/Button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/Card/Card';
import { REGISTER_QUERY_KEY } from '@/features/auth/api/register';
import { RegistrationForm } from '@/features/auth/components/RegistrationForm/RegistrationForm';
import { useIsMutating } from '@tanstack/react-query';
import type { DecodedInvitationToken } from '../../types';

interface AccountSetupStepProps {
  decodedToken: DecodedInvitationToken;
  token: string;
}

export const AccountSetupStep = ({ decodedToken, token }: AccountSetupStepProps) => {
  const { t } = useTranslation('auth', {
    keyPrefix: 'register.accountSetupStep',
  });

  const isRegistrationLoading = !!useIsMutating({ mutationKey: [REGISTER_QUERY_KEY] });

  const username = decodedToken.UserName;

  return (
    <GenericLayout
      mainProps={{
        className: 'max-tablet:justify-start',
      }}
    >
      <Head title={t('seo.title')} />
      <Card className='tablet:w-[460px] desktop:w-[480px] max-tablet:p-5 max-tablet:pb-0 max-tablet:min-h-[calc(100vh-76px)] w-full'>
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
          <CardDescription>{t('subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className='tablet:mb-6 mb-8'>
          <RegistrationForm username={username} invitationToken={token} />
        </CardContent>
        <CardFooter isStickyOnMobile>
          <Button
            type='submit'
            className='w-full'
            form='account-setup-form'
            isLoading={isRegistrationLoading}
            disabled={isRegistrationLoading}
            aria-busy={isRegistrationLoading}
          >
            {t('submit.label')}
          </Button>
        </CardFooter>
      </Card>
    </GenericLayout>
  );
};

export default AccountSetupStep;
